// Guard global: o botão "voltar" do aparelho (Android) / gesto de voltar
// fecha o modal aberto em vez de navegar para a tela anterior.
//
// Como funciona
// -------------
// Quase todos os modais do app são overlays anexados ao <body> com a classe
// `.settings-modal` ou `.modal-overlay` (alguns extras: bottom sheet do mapa,
// popups). Em vez de instrumentar os ~28 modais um a um, observamos o <body>:
//
//   1. Quando o PRIMEIRO modal abre, empurramos um estado no history
//      (pushState). Isso cria um "degrau" extra: o próximo "voltar" cai nesse
//      degrau em vez de sair da página.
//   2. No `popstate` (voltar pressionado) com modal aberto, fechamos o modal
//      do topo — sem navegar. Se ainda sobrar modal, o observer re-arma o
//      degrau automaticamente.
//   3. Quando o modal fecha normalmente (X, salvar, Esc, clique fora), o
//      degrau extra é removido com um history.back() silencioso, pra não
//      deixar lixo na pilha de navegação.
//
// Fechamento genérico: disparamos um clique no backdrop do overlay do topo
// (a maioria fecha com `e.target === overlay`); se não fechar, tentamos um
// botão de dispensa explícito e, por fim, a tecla Esc. Tudo escopado ao
// overlay do topo pra fechar um de cada vez.

// Overlays considerados "modais" pra esse guard. Cobre os padrões reais do
// app. NÃO inclui toasts / loading overlays (não são dispensáveis por voltar).
const MODAL_SELECTOR = [
  '.settings-modal',
  '.modal-overlay',
  '.map-bottom-sheet',
  '[role="dialog"][aria-modal="true"]',
].join(',');

let installed = false;

/** Overlays de modal de fato VISÍVEIS, na ordem do documento (topo = último).
 *
 * Alguns elementos que casam o seletor ficam SEMPRE no DOM, apenas escondidos
 * (`hidden`/display:none) — o sheet do mapa (`.map-bottom-sheet`) e o sheet
 * "Mais" (role=dialog dentro de um overlay `hidden`, presente em toda página).
 * Contá-los como "abertos" fazia o guard armar o degrau o tempo todo e o botão
 * voltar não navegar (tentava "fechar" um modal já fechado). Por isso filtramos
 * por visibilidade real (`checkVisibility` cobre ancestrais escondidos). */
function isVisible(el) {
  if (!el.isConnected) return false;
  if (typeof el.checkVisibility === 'function') return el.checkVisibility();
  return !el.hidden;
}

function openModalOverlays() {
  return Array.from(document.querySelectorAll(MODAL_SELECTOR)).filter(isVisible);
}

/** Fecha o modal do topo de forma genérica, escopado a ele. */
function closeTopmostModal() {
  const overlays = openModalOverlays();
  if (overlays.length === 0) return;
  const top = overlays[overlays.length - 1];

  // 1) Clique no backdrop — a maioria fecha com `e.target === overlay`.
  top.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

  // 2) Não fechou? Tenta um controle de dispensa explícito dentro do overlay.
  if (top.isConnected) {
    const btn = top.querySelector(
      '[data-action="close"], [data-action="cancel"], [data-action="done"], .newtrip-close, [aria-label="Fechar"]',
    );
    if (btn) { try { btn.click(); } catch { /* noop */ } }
  }

  // 3) Última tentativa: tecla Esc (muitos modais escutam no document).
  if (top.isConnected) {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }
}

export function installModalBackGuard() {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!window.history?.pushState) return;
  installed = true;

  // Temos um degrau de history empurrado por nós?
  let guardArmed = false;
  // Estamos removendo nosso próprio degrau (history.back silencioso)?
  let cleaningUp = false;

  function armGuard() {
    if (guardArmed) return;
    try {
      window.history.pushState({ __modalGuard: true }, '');
      guardArmed = true;
    } catch { /* alguns ambientes restringem pushState */ }
  }

  function disarmGuard() {
    if (!guardArmed) return;
    guardArmed = false;
    cleaningUp = true;
    try {
      window.history.back();   // remove nosso degrau extra
    } catch {
      cleaningUp = false;
    }
  }

  // Reage à abertura/fechamento de modais (contagem sobe/desce).
  function syncToModalState() {
    const count = openModalOverlays().length;
    if (count > 0) {
      armGuard();
    } else {
      disarmGuard();
    }
  }

  window.addEventListener('popstate', () => {
    // Foi nosso back() de limpeza — só consome e sai.
    if (cleaningUp) { cleaningUp = false; return; }

    // Voltar pressionado com modal aberto → consumimos o degrau e fechamos.
    if (guardArmed && openModalOverlays().length > 0) {
      guardArmed = false;      // o degrau foi consumido por este popstate
      closeTopmostModal();
      // Se ainda restar modal (modais empilhados), o observer abaixo re-arma.
      // Damos um tick pro DOM se atualizar e re-sincronizamos.
      setTimeout(syncToModalState, 0);
    }
  });

  // Observa adições/remoções no <body> pra saber quando modais abrem/fecham.
  const observer = new MutationObserver(() => syncToModalState());
  observer.observe(document.body, { childList: true, subtree: true });

  // Estado inicial (caso já haja um modal montado no load).
  syncToModalState();
}

// Auto-instala ao importar (idempotente). Adia até o body existir.
if (typeof document !== 'undefined') {
  if (document.body) {
    installModalBackGuard();
  } else {
    document.addEventListener('DOMContentLoaded', installModalBackGuard, { once: true });
  }
}
